<template>
  <v-container fluid class="fill-height">
    <v-row class="">
      <!-- Left Side - Image Background -->
      <v-col
        cols="12"
        md="7"
        class="d-flex align-center justify-center Login-Image-Background"
      >
      </v-col>

      <v-col
        cols="12"
        md="5"
        class="d-flex align-center justify-center py-4 px-4"
      >
        <v-card
          flat
          class="pa-6 rounded-lg elevation-0"
          width="100%"
          max-width="450"
          style="
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
          "
        >
          <v-card-title class="d-flex justify-center pb-2">
            <img
              class="Brand-Logo mb-3"
              alt="Brand Logo"
              src="@/assets/images/brand-logo.png"
              style="max-width: 150px; height: auto"
            />
          </v-card-title>

          <v-card-text class="text-center pb-2">
            <h2 class="font-weight-bold text-h5 primary--text mb-2">
              Welcome Back
            </h2>
          </v-card-text>

          <v-card-text>
            <v-form
              @submit.prevent="submitForm"
              ref="loginForm"
              v-model="isValid"
            >
              <!-- Username Field -->
              <v-text-field
                label="Username"
                required
                outlined
                dense
                v-model="userDetails.username"
                :rules="usernameRules"
                prepend-inner-icon="mdi-account"
                class="mb-2"
                autocomplete="username"
              ></v-text-field>

              <!-- Password Field -->
              <v-text-field
                label="Password"
                required
                outlined
                dense
                v-model="userDetails.password"
                :type="passwordEye ? 'text' : 'password'"
                :append-icon="passwordEye ? 'mdi-eye' : 'mdi-eye-off'"
                :rules="passwordRules"
                @click:append="passwordEye = !passwordEye"
                prepend-inner-icon="mdi-lock"
                class="mb-3"
                autocomplete="current-password"
              ></v-text-field>

              <v-btn
                type="submit"
                color="primary"
                block
                medium
                :loading="btnLoading"
                :disabled="!isValid"
                class="mb-4"
                elevation="2"
              >
                <v-icon left>mdi-login</v-icon>
                Sign In
              </v-btn>
              <a @click="startMicrosoftLogin" class="ms-login-btn">
                  <span class="ms-logo">
                      <span class="red"></span>
                      <span class="green"></span>
                      <span class="blue"></span>
                      <span class="yellow"></span>
                  </span>
                  <span class="ms-text">Sign in with Microsoft</span>
              </a>

              <v-btn
                color="info"
                block
                medium
                class="mb-3"
                elevation="2"
                @click="openWebApp"
              >
                <v-icon left>mdi-steering</v-icon>
                Login as Driver
              </v-btn>

            </v-form>
          </v-card-text>

          <v-card-actions class="d-flex flex-column align-center px-6">
            <v-divider class="my-3 w-100"></v-divider>

            <!-- Grid of action buttons -->
            <v-row class="ma-0 w-100" justify="center" no-gutters>
              <v-col cols="6" sm="6" class="pa-1">
                <v-btn
                  text
                  color="primary"
                  class="text-capitalize w-100"
                  small
                  @click="forgotPassword"
                >
                  <v-icon small left>mdi-key</v-icon>
                  <span class="d-none d-sm-inline">Forgot Password</span>
                  <span class="d-inline d-sm-none">Password</span>
                </v-btn>
              </v-col>

              <v-col cols="6" sm="6" class="pa-1">
                <v-btn
                  text
                  color="primary"
                  class="text-capitalize w-100"
                  small
                  @click="forgotUsername"
                >
                  <v-icon small left>mdi-account-question</v-icon>
                  <span class="d-none d-sm-inline">Forgot Username</span>
                  <span class="d-inline d-sm-none">Username</span>
                </v-btn>
              </v-col>

              <v-col cols="6" sm="6" class="pa-1">
                <v-btn
                  text
                  color="success"
                  class="text-capitalize w-100"
                  small
                  @click="signUp"
                >
                  <v-icon small left>mdi-account-plus</v-icon>
                  <span class="d-none d-sm-inline">New User</span>
                  <span class="d-inline d-sm-none">Sign Up</span>
                </v-btn>
              </v-col>

              <v-col cols="6" sm="6" class="pa-1">
                <v-btn
                  text
                  color="info"
                  class="text-capitalize w-100"
                  small
                  @click="companyRequest"
                >
                  <v-icon small left>mdi-domain</v-icon>
                  <span class="d-none d-sm-inline">Company Request</span>
                  <span class="d-inline d-sm-none">Company</span>
                </v-btn>
              </v-col>

              <!-- New Web App Option -->
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading Overlay -->
    <v-overlay :value="btnLoading" opacity="0.8">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import { encryptText } from "@/utils/functions";

export default {
  components: { InputField },
  layout: "login",
  data() {
    return {
      userDetails: {
        username: "",
        password: "",
      },
      btnLoading: false,
      passwordEye: false,
      isValid: false,
      rememberMe: false,

      usernameRules: [(v) => !!v || "Username is required"],
      passwordRules: [(v) => !!v || "Password is required"],
    };
  },
  mounted() {
    const code = this.$route?.query?.code;
    const error = this.$route?.query?.error;
    if (error) {
      this.handleError({ data: { non_field_errors: ["Microsoft login failed."] } });
      return;
    }
    if (code) {
      this.handleMicrosoftCallback(code);
    }
  },
  methods: {
    startMicrosoftLogin() {
      const base = process.env.VUE_APP_BASE_URL || "";
      window.location.href = `${base}api/accounts/microsoft/login/`;
    },
    handleMicrosoftCallback(code) {
      this.btnLoading = true;
      this.$api.authentication
        .exchangeMicrosoftCode({ code })
        .then(({ data }) => {
          localStorage.setItem("user", data.token);
          this.$api.authentication
            .getProfileInfo()
            .then((res) => {
              localStorage.setItem("userProfile", JSON.stringify(res.data));
              localStorage.setItem("user_type", res.data.user_type || "");
              localStorage.setItem(
                "permissions",
                JSON.stringify((res.data.permissions || {})),
              );
              window.history.replaceState({}, document.title, "/app/login");
              const target = (res.data.user_type || "admin").toLowerCase();
              this.$router.push(`/app/${target}`);
            })
            .catch((err) => {
              this.handleError(err);
            })
            .finally(() => {
              this.btnLoading = false;
            });
        })
        .catch((err) => {
          this.btnLoading = false;
          this.handleError(err);
        });
    },
    submitForm() {
      if (!this.$refs.loginForm.validate()) {
        return;
      }

      this.btnLoading = true;

      let payload = {
        ...this.userDetails,
      };

      if (this.userDetails && this.userDetails.password) {
        payload.password = encryptText(this.userDetails.password);
      }

      this.$api.authentication
        .login(payload)
        .then(({ data }) => {
          // Store user data
          localStorage.setItem("user_type", data.user_type);
          localStorage.setItem("user", data.token);
          localStorage.setItem(
            "permissions",
            JSON.stringify(data.detail.permissions),
          );

          // Get profile info
          this.$api.authentication
            .getProfileInfo()
            .then((res) => {
              this.$bus.$emit("showLoader", false);
              localStorage.setItem("userProfile", JSON.stringify(res.data));

              const preference =
                res && res.data && res.data.user_preferences
                  ? res.data.user_preferences
                  : {};

              localStorage.setItem(
                "userPreferences",
                JSON.stringify(preference),
              );

              // Show success message
              this.$bus.$emit("showToastMessage", {
                message: "Login successful! Welcome back.",
                color: "success",
              });

              setTimeout(() => {
                if (
                  this.$route &&
                  this.$route.query &&
                  this.$route.query.redirectUrl
                ) {
                  this.$router.push(this.$route.query.redirectUrl);
                } else {
                  this.$router.push(`/app/${data.user_type.toLowerCase()}`);
                }
              }, 500);
            })
            .catch((err) => {
              this.$bus.$emit("showLoader", false);
              this.handleError(err);
            });

          this.btnLoading = false;
        })
        .catch((err) => {
          this.btnLoading = false;
          this.handleError(err);
        });
    },

    handleError(err) {
      let errorMessage = "Unable to log in with provided credentials.";

      if (err && err.data) {
        if (err.data.non_field_errors && err.data.non_field_errors.length > 0) {
          errorMessage = err.data.non_field_errors[0];
        } else if (err.data.detail) {
          errorMessage = err.data.detail;
        } else if (err.data.message) {
          errorMessage = err.data.message;
        }
      }

      this.$bus.$emit("showToastMessage", {
        message: errorMessage,
        color: "error",
        hasActions: true,
      });
    },

    forgotPassword() {
      this.$router.push("/app/forgot-password");
    },

    forgotUsername() {
      this.$router.push("/app/forgot-username");
    },

    signUp() {
      this.$router.push("/app/signup");
    },

    companyRequest() {
      this.$router.push("/app/company-request");
    },

    openWebApp() {
      const baseUrl = window.location.origin + "/app/";
      window.open(baseUrl);
    },
  },
};
</script>

<style scoped>
.Screen-Height {
  height: 100vh;
}

.Brand-Logo {
  width: 100%;
  max-width: 150px;
  height: auto;
  object-fit: contain;
}

.Login-Image-Background {
  background: url("@/assets/images/brand-logo1.png") no-repeat center center;
  background-size: cover;
  min-height: 100vh;
  padding: 20px;
}

.welcome-overlay {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  padding: 2rem;
  max-width: 500px;
}
.driver-login-link {
  color: #1976d2;
  text-decoration: none;
  font-size: 0.78rem;
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s;
  line-height: 1.2;
}

.driver-login-link:hover {
  opacity: 0.75;
  text-decoration: underline;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .Login-Image-Background {
    min-height: 40vh;
    background-attachment: scroll;
  }

  .welcome-overlay h1 {
    font-size: 1.8rem !important;
  }

  .Brand-Logo {
    max-width: 120px;
  }
}

@media (max-width: 600px) {
  .Login-Image-Background {
    min-height: 30vh;
  }

  .welcome-overlay {
    padding: 1rem;
  }

  .welcome-overlay h1 {
    font-size: 1.5rem !important;
  }
}

/* Enhanced button styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
}

.v-btn--outlined {
  border-width: 2px !important;
}

/* Custom card styling */
.v-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Loading overlay styling */
.v-overlay {
  z-index: 9999;
}

.ms-login-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;

      height: 44px;
      padding: 0 16px;
      margin: 0 auto 16px auto;

      background: #ffffff;
      border: 1px solid #c7c7c7;
      border-radius: 4px;

      font-family: "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #5e5e5e;

      text-decoration: none;
      cursor: pointer;
  }

  .ms-login-btn:hover {
      background: #f5f5f5;
  }

  .ms-logo {
      display: grid;
      grid-template-columns: repeat(2, 10px);
      grid-template-rows: repeat(2, 10px);
      gap: 2px;
  }

  .ms-logo span {
      width: 10px;
      height: 10px;
      display: block;
  }

  .ms-logo .red    { background: #F25022; }
  .ms-logo .green  { background: #7FBA00; }
  .ms-logo .blue   { background: #00A4EF; }
  .ms-logo .yellow { background: #FFB900; }
  
</style>
